/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {AfterViewInit, Directive, EventEmitter, Input, Output} from '@angular/core';
import {EntityElementModel} from '../../../shared/entity-tree/entity-element/models/entity-element.model';
import {EntityTreeBranchModel} from '../../../shared/entity-tree/models/entity-tree-branch.model';
import {ReplaySubject} from 'rxjs';
import {InputMessagesService} from '../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {EntityElementActionsEnum} from '../../../shared/entity-tree/entity-element/models/entity-element-actions.enum';
import {InputTypeEnum} from '../../../shared/input/input-type.enum';
import {InputListModel} from './input-list.model';
import {BusinessSettingsModalState} from './business-settings-modal.state';
import {InputComponent} from '../../../shared/input/input/input.component';
import {EntityTreeBranchDisplayDataModel} from '../configuration/v1/models/entity-tree-branch-display-data.model';
import {TreeNodeModel} from '../models/tree-node-model';

@Directive()
export abstract class BusinessSettingsModal<T extends BusinessSettingsModalState = BusinessSettingsModalState>
        implements AfterViewInit {
    /**
     * <p>The complete relation tree TreeNodeModel.
     */
    @Input() configurationTreeNode: TreeNodeModel;
    /**
     * <p>Array of entities already on the pool.
     */
    @Input() excludedEntities: EntityElementModel[] = [];
    /**
     * <p>The node where this modal is used.
     */
    @Input() entityNode: EntityTreeBranchModel;
    /***
     * <p>The display data for the node where this modal is used
     */
    @Input() entityDisplayData: EntityTreeBranchDisplayDataModel;
    /**
     * <p>Changes the state of the modal between editing a value or adding a new one.
     */
    @Input() isEdit: boolean;
    /**
     * <p>Emit the {@link EntityElementModel} to edit on a pool.
     */
    @Output() edit: EventEmitter<EntityElementModel>;
    /**
     * <p>Emit the {@link EntityElementModel} to add to a pool.
     */
    @Output() add: EventEmitter<EntityElementModel>;
    /**
     * <p>Enables the submit action (edit/add) on the modal.
     */
    isValid = false;
    /**
     * <p>{@link WarningsEnum} used in the template.
     */
    WarningsEnum = WarningsEnum;
    /**
     * <p>Emits once, for any subscriber that subscribes before or after the subscription has already completed,
     * when the modal is ready.
     */
    protected onReady = new ReplaySubject<boolean>(1);
    /**
     * <p>An array containing all the inputs on the modal.
     */
    private inputs: InputListModel[];
    /**
     * <p>The modal entity name.
     */
    abstract ENTITY_NAME: string;
    /**
     * <p>The modal title when in edit mode.
     */
    abstract EDIT_TITLE: string;
    /**
     * <p>The modal title when in add mode.
     */
    abstract ADD_TITLE: string;
    /**
     * <p>The modal submit button label when in edit mode.
     */
    abstract EDIT_SUBMIT_TITLE: string;
    /**
     * <p>The modal submit button label when in add mode.
     */
    abstract ADD_SUBMIT_TITLE: string;

    /**
     * <p>State of the modal.
     */
    public _state: T;

    get state(): T {
        return this._state;
    }

    get title(): string {
        return this.isEdit ? this.EDIT_TITLE : this.ADD_TITLE;
    }

    get primaryActionTitle(): string {
        return this.isEdit ? this.EDIT_SUBMIT_TITLE : this.ADD_SUBMIT_TITLE;
    }

    public constructor(protected inputMessages: InputMessagesService) {
        this.storeState();
    }

    // TODO: CTWILS-3156 - update this class to use StatefulComponent
    storeState(state?: Partial<BusinessSettingsModalState>): void {
        const baseState: any = {
            isActive: false
        };

        // `Object.assign` instead of spread operator on generics, it was introduced in later TypeScript versions
        this._state = Object.assign(baseState, this._state, state);
    }

    /**
     * <p>Retrieve data needed by this modal, may it be from the back-end or deduced from already existing.
     */
    getInitialData(): void {
    }

    /**
     * <p>AfterViewInit should call {@link onReady.next(true)}, {@link onReady.complete()} and set the {@link inputs} value
     */
    ngAfterViewInit(): void {
        this.onReady.next(true);
        this.onReady.complete();

        this.inputs = this.generateInputs();
    }

    /**
     * <p>Maps the modal data to an {@link EntityElementModel}.
     */
    mapEntity(): EntityElementModel {
        return {
            name: this.state.currentValue.name,
            code: this.state.currentValue.code,
            hasDisplayCode: this.entityNode.hasDisplayCode,
            displayCode: this.entityNode.displayCodeFunction != null ?
                    this.entityNode.displayCodeFunction(this.state.currentValue) : this.state.currentValue.code,
            sortingCode: this.state.currentValue.name,
            active: this.state.isActive,
            parentCode: this.state.originalElement != null ? this.state.originalElement.parentCode : null,
            detail: this.state.originalElement != null ? this.state.originalElement.detail : null,
            action: EntityElementActionsEnum.EDIT,
            displayParentCode: this.state.originalElement != null ? this.state.originalElement.displayParentCode : null,
            connectedWithParent: this.state.originalElement != null ? this.state.originalElement.connectedWithParent : false,
            isSelected: this.state.originalElement != null ? this.state.originalElement.isSelected : false,
            contextMenuActions: this.state.originalElement != null ?
                    this.state.originalElement.contextMenuActions : this.entityDisplayData?.contextMenuActions,
            childrenCode: this.entityNode.childrenCodeFunction != null ?
                    this.entityNode.childrenCodeFunction(this.state.currentValue) : this.state.currentValue.code,
            iconCode: this.iconCode(),
            originalValue: this.state.originalValue != null ? this.state.originalValue : this.state.currentValue,
            currentValue: this.state.currentValue,
            breadcrumbLabel: this.entityNode.breadcrumbLabelFunction != null ?
                    this.entityNode.breadcrumbLabelFunction(this.state.currentValue) : null
        };
    }

    /**
     * <p>Emits the submit action
     */
    save(): void {
        const element = this.mapEntity();

        if (this.isEdit) {
            this.edit.emit(element);

        } else {
            this.add.emit(element);
        }
    }

    /**
     * <p> Sets the active input state
     */
    setActiveInputState(state: boolean, isSetInitValue?: boolean): void {
        this.storeState({isActive: state});

        this.setModalValidity(isSetInitValue);
    }

    /**
     * <p>Sets the modal validity
     */
    setModalValidity(isSetInitialValue?: boolean): void {
        let valid = true;
        for (let i = 0; i < this.inputs.length; i++) {
            switch (this.inputs[i].type) {
                case InputTypeEnum.DROPDOWN:
                    valid = this.isValidInputDropDown(this.inputs[i]);
                    break;
                case InputTypeEnum.TEXT:
                    valid = this.isValidInputText(this.inputs[i]);
                    break;
                default:
                    valid = this.inputs[i].input.isValid;
                    break;
            }
            if (!valid) {
                break;
            }
        }
        this.isValid = valid && !isSetInitialValue;
    }

    manageAlreadyExistsCode(code: string, entity: string, messagesMap: Map<WarningsEnum, string[]>,
                            input: InputComponent): void {
        let usedCodes = this.excludedEntities.map((e) => e.code);

        if (this.isEdit) {
            usedCodes = usedCodes.filter((e) => e !== this.state.originalElement.code);
        }

        if (code != null && usedCodes.includes(code)) {
            messagesMap.set(WarningsEnum.ERROR, [`That ${entity} code already exists`]);
            input.markAsInvalid();
        } else {
            messagesMap.delete(WarningsEnum.ERROR);
        }
    }

    manageAlreadyExistsName(name: string, entity: string, messagesMap: Map<WarningsEnum, string[]>,
                            input: InputComponent): void {
        let usedNames = this.excludedEntities.map((e) => e.name?.toUpperCase());

        if (this.isEdit) {
            usedNames = usedNames.filter((e) => e !== this.state.originalElement.name?.toUpperCase());
        }

        if (name != null && usedNames.includes(name?.toUpperCase())) {
            messagesMap.set(WarningsEnum.ERROR, [`That ${entity} already exists`]);
            input.markAsInvalid();
        } else {
            messagesMap.delete(WarningsEnum.ERROR);
        }
    }

    private isValidInputDropDown(toValidate: InputListModel): boolean {
        let valid: boolean;
        if (toValidate.mandatory) {
            valid = toValidate.input.inputControl.value != null
                    && toValidate.input.inputControl.value.constructor !== ''.constructor
                    && toValidate.input.isValid;
        } else {
            valid = toValidate.input.inputControl.value == null || toValidate.input.inputControl.value.length === 0
                    || (toValidate.input.inputControl.value.constructor !== ''.constructor
                            && toValidate.input.isValid);
        }

        return valid;
    }

    private isValidInputText(toValidate: InputListModel): boolean {
        let valid: boolean;
        if (toValidate.mandatory) {
            valid = toValidate.input.inputControl.value != null
                    && toValidate.input.inputControl.value.length > 0
                    && toValidate.input.isValid;
        } else {
            valid = toValidate.input.inputControl.value == null
                    || toValidate.input.inputControl.value.length === 0
                    || toValidate.input.isValid;
        }

        return valid;
    }

    private iconCode(): string {
        if (this.entityNode.hasIconFunction != null) {
            return this.entityNode.hasIconFunction(this.state.currentValue) ? this.entityNode.iconCode : null;
        }
        return null;
    }

    /**
     * <p>Set the value on a modal.
     * @param value The value to set on the edit modal.
     */
    abstract setEntity(value: EntityElementModel): void;

    /**
     * <p>Generates the content for {@link inputs}.
     */
    protected abstract generateInputs(): InputListModel[];

}

